import { motion } from "framer-motion";
import { Quotes } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";
import useSiteSettings from "@/hooks/useSiteSettings";

const DEFAULT_ABOUT_IMG =
    "https://images.pexels.com/photos/15337739/pexels-photo-15337739.jpeg";

const DEFAULT_STATS = [
    { value: "2018", label: "Founded" },
    { value: "100k+", label: "Plates served" },
    { value: "100%", label: "Family-run" },
];

const DEFAULT_QUOTE = {
    text: "\"From abuela's kitchen to Foxon Blvd.\"",
    author: "The J&W Family",
};

export default function About() {
    const { t, lang } = useI18n();
    const { settings } = useSiteSettings();
    const aboutImg = settings.about_image_url || DEFAULT_ABOUT_IMG;
    const a = settings.about || {};

    // Per-language helpers: pick owner override, else fall back to i18n.
    const title =
        (lang === "en" ? a.title_en : a.title_es) || t("about.title");
    const body =
        (lang === "en" ? a.body_en : a.body_es) || t("about.body");
    const quote =
        (lang === "en" ? a.quote_en : a.quote_es) || DEFAULT_QUOTE.text;
    const quoteAuthor = a.quote_author || DEFAULT_QUOTE.author;
    const stats =
        a.stats && a.stats.length > 0
            ? a.stats.filter((s) => s.value || s.label)
            : DEFAULT_STATS;

    return (
        <section id="about" data-testid="about" className="bg-muted py-20 md:py-28">
            <div className="mx-auto w-full max-w-7xl px-4 md:px-8 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary">
                        {t("nav.about")}
                    </p>
                    <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl mt-3">
                        {title}
                    </h2>
                    <Quotes size={48} weight="fill" className="text-primary mt-8" />
                    <p className="mt-3 text-lg leading-relaxed text-muted-foreground max-w-xl whitespace-pre-line">
                        {body}
                    </p>
                    <div
                        className="mt-8 grid gap-4 max-w-md"
                        style={{ gridTemplateColumns: `repeat(${Math.min(stats.length, 4)}, minmax(0, 1fr))` }}
                    >
                        {stats.slice(0, 4).map((s, i) => (
                            <Stat key={i} k={s.value} l={s.label} />
                        ))}
                    </div>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="relative aspect-[4/5] rounded-3xl overflow-hidden"
                >
                    <img
                        src={aboutImg}
                        alt="Inside J&W Deli"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur rounded-2xl p-5">
                        <p className="font-display text-xl font-black tracking-tight">
                            {quote}
                        </p>
                        <p className="text-xs uppercase tracking-widest text-muted-foreground mt-2">
                            — {quoteAuthor}
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

function Stat({ k, l }) {
    return (
        <div>
            <p className="font-display font-black text-3xl">{k}</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{l}</p>
        </div>
    );
}
